import { isDataSaving } from "./environment";
import mutations from "./mutations";
import notifier from "./airbrake";

const AFTER_LOAD_EVENT = "afterload";
const ATTRIBUTE = "data-defer";
const ERROR_EVENT = "error";
const INTERSECTION_CONFIG = {
  root: null,
  rootMargin: "150% 0px",
  threshold: 0
};
const LOAD_EVENT = "load";
const SELECTOR = `picture[${ATTRIBUTE}]`;
const TRANS = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

const supportsLoading = "loading" in HTMLImageElement.prototype && false;

let intersectionObserver;

function notify({ message, type }) {
  notifier.notify({
    error: {
      message,
      type
    }
  });
}

function isImageApplied(target) {
  if ('error' in target.dataset) {
    const { srcset } = target;
    let message = srcset;

    if (!srcset || srcset === TRANS) {
      message = `${!srcset ? "<not set>" : "<transparent>"} alt="${target.alt}"`;
    }

    notify({
      message: `Image load error: ${message}`,
      type: "LoadError"
    });
  }

  return (
    // the load errored
    'error' in target.dataset ||
    // we only ever wanted TRANS anyway
    target.srcset === TRANS ||
    (target instanceof HTMLImageElement && target.src === TRANS) ||
    // currentSrc has changed
    target.currentSrc !== TRANS
  );
}

function observeNode(node) {
  intersectionObserver.observe(node);
}

function onimageload({ target }) {
  if (isImageApplied(target)) {
    target.removeEventListener(LOAD_EVENT, onimageload);
    if (target.picture) {
      target.picture.removeAttribute(ATTRIBUTE);
    } else {
      notify({
        message: `Picture reference lost: alt="${target.alt}"`,
        type: "PictureError"
      });
    }

    target.removeAttribute("loading");
    const { dataset } = target;

    target.alt = dataset.alt;
    delete dataset.alt;

    window.dispatchEvent(new CustomEvent(AFTER_LOAD_EVENT, {
      detail: { src: target }
    }));
  }

  // Additional: report to Airbrake if PictureBox image is being used
  if (/\/pb\//.test(target.srcset)) {
    notify({
      message: `Picturebox image used: ${target.srcset}`,
      type: "ImageError"
    });
  }
}

function onimageerror({ target }) {
  target.dataset.error = "";

  const { picture } = target;

  picture.querySelectorAll("source").forEach(source => {
    picture.removeChild(source);
  });
  target.dispatchEvent(new Event(LOAD_EVENT));
}

function processSrcset(element) {
  const { dataset } = element;
  const { srcset } = dataset;

  if (srcset.substr(0, 2) === "//") {
    // protocol agnostic, fix to HTTPS
    dataset.srcset = `https:${srcset}`;
  }

  element.srcset = dataset.srcset;
  delete dataset.srcset;
}

function processImage(target, immediate) {
  const img = target.querySelector("img");
  const { fetchPriority = "auto" } = img;

  img.dataset.alt = img.alt;
  img.alt = "";
  img.picture = target; // memoise to avoid multiple `.closest` calls
  img.addEventListener(ERROR_EVENT, onimageerror, { once: true });
  img.addEventListener(LOAD_EVENT, onimageload);
  img.loading = immediate ? "eager" : "lazy"; // if supported use loading attribute value
  if (fetchPriority === "auto" && immediate) {
    img.fetchPriority = "high";
  }
}

function processDataSaving(srcsets) {
  let result = [];

  if (srcsets.length) {
    const last = srcsets.pop();

    srcsets.forEach(srcset => {
      srcset.parentNode.removeChild(srcset);
    });

    if (last.src === TRANS) {
      result = [last];
    }
  }

  return result;
}

function processEntry(target) {
  const { immediate } = target.dataset;
  let srcsets = target.querySelectorAll("[data-srcset]");

  if (isDataSaving()) {
    srcsets = processDataSaving([].slice.call(srcsets));
  }

  processImage(target, immediate);
  srcsets.forEach(processSrcset);
  if (!supportsLoading) {
    intersectionObserver.unobserve(target);
  }
}

function processNodes(nodes) {
  if (supportsLoading) {
    nodes.forEach(processEntry);
  } else {
    nodes.forEach(observeNode);
  }
}

function processNode(node) {
  const origin = node.parentNode || node;
  const deferred = [].slice.call(origin.querySelectorAll(SELECTOR));

  if (origin.dataset.defer) {
    deferred.unshift(origin);
  }

  if (deferred.length) {
    processNodes(deferred);
  }
}

function onintersection(entries) {
  entries.filter(entry => entry.isIntersecting).forEach(({ target }) => {
    processEntry(target);
  });
}

function force(container) {
  if (container) {
    container.querySelectorAll(SELECTOR).forEach(element => {
      element.dataset.immediate = true;
      window.requestAnimationFrame(() => {
        processEntry(element);
      });
    });
  }
}

function init() {
  processNodes(document.querySelectorAll(SELECTOR));
}

if (!supportsLoading) {
  intersectionObserver = new IntersectionObserver(onintersection, INTERSECTION_CONFIG);
}

document.addEventListener("DOMContentLoaded", init);

mutations.add(SELECTOR, processNode);

export default force;
export { AFTER_LOAD_EVENT, LOAD_EVENT };



// WEBPACK FOOTER //
// ./shared/helpers/defer-picture.js